/**
 * Helpers para detectar o erro 409 `MANAGER.USUARIOS.TIMES_JA_ATRIBUIDOS`
 * retornado pelo backend Node quando o fluxo de criar/editar gestor tenta
 * atribuir times que ja pertencem a outro gestor.
 *
 * Contexto (BUG-017, regressivo-pos-migracao 2026-07-29):
 *
 * BUG-016 fechou o gap em que `POST /api/v1/usuarios` ignorava
 * `responsaveisIds`/`timesIds`. Ao ativar a criacao real dos vinculos, o
 * banco passou a rejeitar (com o UNIQUE `uq_vh_filho_principal_ativo`) o
 * caso em que 2 gestores tentam ser o "principal" do mesmo time — situacao
 * comum quando o admin escolhe um time do multiselect sem saber que ele
 * ja pertence a outro gestor.
 *
 * Decisao (Marcos + @Tony): em vez de bloquear com erro cru, o backend
 * retorna 409 estruturado listando os conflitos, e o fed exibe modal de
 * confirmacao "transferir?". Se o admin confirmar, refaz a chamada com
 * `forcarTransferencia: true` — que o backend interpreta como "OK, limpa
 * o vinculo principal do gestor antigo e transfere pra este gestor novo".
 *
 * Este helper centraliza a deteccao do 409 estruturado (extrai lista de
 * conflitos ou devolve `null`) para nao espalhar `error?.error?.codigo ===
 * '...'` pelos varios pontos de submit (SHARED colaborador-form-modal +
 * aba-hierarquia editar), o que seria terreno ferti para regressao.
 *
 * O disparo do modal de confirmacao + o re-submit ficam no componente
 * consumidor (contrato de UI + rebinding de payload sao especificos por
 * fluxo). Este helper e a "detecao pura".
 */

/**
 * Codigo unico do envelope de erro Manager para o caso "time ja pertence
 * a outro gestor — confirmar transferencia?". Definido por @Tony no
 * contrato do backend Node (BUG-017).
 */
export const TIMES_JA_ATRIBUIDOS_CODIGO = 'MANAGER.USUARIOS.TIMES_JA_ATRIBUIDOS';

/**
 * Shape de cada item em `error.error.detalhes.timesConflito`.
 * Todos os campos vem do backend Node — nao inventar mais no fed.
 */
export interface TimeConflito {
  readonly timeId: number;
  readonly timeNome: string;
  readonly gestorAtualId: number;
  readonly gestorAtualNome: string;
}

/**
 * Shape parcial do envelope de erro Manager (Node). Reusa a mesma
 * cascata de `error.utils.ts` — mas aqui checamos o `codigo` semantico
 * e os `detalhes` (que o extractor generico nao le).
 */
interface ManagerConflitoErrorLike {
  error?: {
    codigo?: string | null;
    mensagem?: string | null;
    detalhes?: {
      timesConflito?: unknown;
    } | null;
  } | null;
  status?: number;
}

/**
 * Testa se o erro recebido e o 409 `TIMES_JA_ATRIBUIDOS` do Manager e,
 * em caso positivo, devolve a lista de conflitos. Caso contrario devolve
 * `null` para que o consumidor propague o erro pelo caminho normal (toast
 * generico via `extractManagerErrorMessage`).
 *
 * Regras de validacao (defensivas — o backend E a fonte da verdade, mas
 * um wire mal-formado nao pode explodir o fed):
 *
 * 1. `error?.error?.codigo === TIMES_JA_ATRIBUIDOS_CODIGO` (match exato)
 * 2. `detalhes.timesConflito` precisa ser um array nao vazio
 * 3. Cada item precisa ter os 4 campos com os tipos esperados
 *    (`timeId:number`, `timeNome:string`, `gestorAtualId:number`,
 *    `gestorAtualNome:string`). Item invalido = array inteiro rejeitado
 *    (nao mostrar modal parcial — melhor cair no toast generico).
 *
 * @param err  qualquer objeto de erro (HttpErrorResponse, Error, unknown)
 * @returns lista de conflitos se for o 409 estruturado; `null` caso contrario.
 */
export function extractTimesJaAtribuidosConflitos(
  err: unknown
): TimeConflito[] | null {
  const e = err as ManagerConflitoErrorLike | null | undefined;
  if (!e?.error) return null;
  if (e.error.codigo !== TIMES_JA_ATRIBUIDOS_CODIGO) return null;

  const raw = e.error.detalhes?.timesConflito;
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const conflitos: TimeConflito[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') return null;
    const c = item as Record<string, unknown>;
    if (
      typeof c['timeId'] !== 'number' ||
      typeof c['timeNome'] !== 'string' ||
      typeof c['gestorAtualId'] !== 'number' ||
      typeof c['gestorAtualNome'] !== 'string'
    ) {
      return null;
    }
    conflitos.push({
      timeId: c['timeId'],
      timeNome: c['timeNome'],
      gestorAtualId: c['gestorAtualId'],
      gestorAtualNome: c['gestorAtualNome']
    });
  }
  return conflitos;
}
